import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { ManageAttendanceFilters as ManageAttendanceFiltersValue } from '../../types/manage-attendance.types';

/** Radix Select cannot hold an empty-string item, so "no filter" is this sentinel. */
const ALL = '__all__';

interface StatusFilterOption {
  value: string;
  labelKey: string;
}

interface StatusSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}

const StatusSelect = ({ value, onValueChange, placeholder, options }: StatusSelectProps) => (
  <Select value={value || ALL} onValueChange={(next) => onValueChange(next === ALL ? '' : next)}>
    <SelectTrigger className="w-full min-w-0">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

interface ManageAttendanceFiltersProps {
  value: ManageAttendanceFiltersValue;
  onChange: (next: ManageAttendanceFiltersValue) => void;
  statusOptions?: StatusFilterOption[];
  /** Show the day picker (organization days, "YYYY-MM-DD", up to `maxDate`); empty means every day. */
  showDate?: boolean;
  maxDate?: string;
}

export const ManageAttendanceFilters = ({
  value,
  onChange,
  statusOptions,
  showDate,
  maxDate,
}: ManageAttendanceFiltersProps) => {
  const { t } = useTranslation();

  return (
    // Two columns on phones/tablets (a lone last control spans both), four on desktop, so the
    // filter block stays compact instead of stacking a full-width row each. The name and department
    // are filtered from their columns (the list keeps the reviewer's department scope).
    <div className="grid w-full min-w-0 grid-cols-2 gap-2 lg:grid-cols-4 [&>*]:min-w-0 [&>:last-child:nth-child(odd)]:col-span-2 lg:[&>:last-child:nth-child(odd)]:col-span-1">
      {showDate && (
        <DatePicker
          className="w-full"
          value={value.date}
          max={maxDate}
          onChange={(date) => onChange({ ...value, date })}
          aria-label={t('DATE')}
        />
      )}

      {statusOptions && (
        <StatusSelect
          value={value.status}
          onValueChange={(status) => onChange({ ...value, status })}
          placeholder={t('STATUS')}
          options={statusOptions.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
          }))}
        />
      )}
    </div>
  );
};
